// components/ModalVacaciones.jsx

import { useState, useEffect } from "react";
import RegistrarVacacionManual from "./RegistrarVacacionManual";
import ModalVerPDF from "./ModalVerPDF";

const API_URL = process.env.REACT_APP_API_URL;

const TABLA = [
  { label: "1", dias: 12 },
  { label: "2", dias: 14 },
  { label: "3", dias: 16 },
  { label: "4", dias: 18 },
  { label: "5", dias: 20 },
  { label: "6 a 10", dias: 22 },
  { label: "11 a 15", dias: 24 },
  { label: "16 a 20", dias: 26 },
  { label: "21 a 25", dias: 28 },
  { label: "26 a 30", dias: 30 },
];

export default function ModalVacaciones({
  usuario,
  token,
  onClose,
  currentUser,
}) {
  const esPropioUsuario = usuario?.id === currentUser?.id;
  const isAdmin = currentUser.rol === "admin";
  const isGerente = currentUser.rol === "gerente";
  const isRRHH = currentUser.rol === "rrhh";
  const [info, setInfo] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(esPropioUsuario ? "solicitar" : "historial");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [diasCalculados, setDiasCalculados] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [comentario, setComentario] = useState("");
  const [solicitudResponder, setSolicitudResponder] = useState(null);
  const [error, setError] = useState("");
  const [exito, setExito] = useState("");
  const [sabadosDetectados, setSabadosDetectados] = useState([]);
  const [sabadosLaborables, setSabadosLaborables] = useState([]);
  const [tipoSolicitud, setTipoSolicitud] = useState("normal");
  const [loadingRep, setLoadingRep] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfTitulo, setPdfTitulo] = useState("");
  const [pdfRow, setPdfRow] = useState(null);
  const puedeAprobar = info?.es_jefe_directo;

  useEffect(() => {
    if (!usuario) return;
    cargarDatos();
  }, [usuario?.id]);

  useEffect(() => {
    setTab(esPropioUsuario ? "solicitar" : "historial");
    setError("");
    setExito("");
  }, [usuario?.id]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [resInfo, resSol] = await Promise.all([
        fetch(`${API_URL}/vacaciones/info/${usuario.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/vacaciones/solicitudes/${usuario.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const dataInfo = await resInfo.json().catch(() => ({}));
      const dataSol = await resSol.json().catch(() => []);
      setInfo(dataInfo);
      setSolicitudes(Array.isArray(dataSol) ? dataSol : []);

      if (puedeAprobar) {
        const resPend = await fetch(`${API_URL}/vacaciones/pendientes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const dataPend = await resPend.json().catch(() => []);
        setPendientes(Array.isArray(dataPend) ? dataPend : []);
      }
    } catch {
      setError("No se pudieron cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  // Calcula días hábiles al cambiar fechas
  useEffect(() => {
    if (!fechaInicio || !fechaFin) {
      setDiasCalculados(0);
      setSabadosDetectados([]);
      setSabadosLaborables([]);
      return;
    }

    const [yi, mi, di] = fechaInicio.split("-").map(Number);
    const [yf, mf, df] = fechaFin.split("-").map(Number);
    const inicio = new Date(yi, mi - 1, di);
    const fin = new Date(yf, mf - 1, df);

    // Detecta sábados en el rango
    const sabados = [];
    const actual = new Date(inicio);
    while (actual <= fin) {
      if (actual.getDay() === 6) {
        sabados.push(actual.toISOString().split("T")[0]);
      }
      actual.setDate(actual.getDate() + 1);
    }
    setSabadosDetectados(sabados);
    // Limpia laborables que ya no están en el rango
    setSabadosLaborables((prev) => prev.filter((s) => sabados.includes(s)));
  }, [fechaInicio, fechaFin]);

  // Recalcula días cuando cambian los sábados laborables
  useEffect(() => {
    if (!fechaInicio || !fechaFin) return;
    // Llama al backend para calcular con sábados
    fetch(`${API_URL}/vacaciones/calcular-dias`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fecha_inicio: fechaInicio,
        fecha_regreso: fechaFin,
        sabados_laborables: sabadosLaborables,
      }),
    })
      .then((r) => r.json())
      .then((d) => setDiasCalculados(d.dias ?? 0))
      .catch(() => {});
  }, [fechaInicio, fechaFin, sabadosLaborables]);

  const toggleSabado = (fecha) => {
    setSabadosLaborables((prev) =>
      prev.includes(fecha) ? prev.filter((s) => s !== fecha) : [...prev, fecha],
    );
  };

  const handleSolicitar = async (e) => {
    e.preventDefault();
    setError("");
    setExito("");

    // Validación de fecha pasada
    const hoy = new Date().toISOString().split("T")[0];
    if (fechaInicio < hoy) {
      setError("La fecha de inicio no puede ser anterior a hoy");
      return;
    }
    if (!fechaInicio || !fechaFin) {
      setError("Selecciona las fechas");
      return;
    }
    if (diasCalculados <= 0) {
      setError("El rango no incluye días hábiles");
      return;
    }

    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/vacaciones`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fecha_inicio: fechaInicio,
          fecha_regreso: fechaFin,
          usuario_id: usuario.id,
          sabados_laborables: sabadosLaborables,
          tipo_solicitud: tipoSolicitud,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message);
      setExito("Solicitud enviada. Se notificó a tu jefe inmediato.");
      setFechaInicio("");
      setFechaFin("");
      setDiasCalculados(0);
      cargarDatos();
    } catch (err) {
      setError(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const handleResponder = async (solicitud_id, estado) => {
    setError("");
    try {
      const res = await fetch(
        `${API_URL}/vacaciones/${solicitud_id}/responder`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ estado, comentario_gerente: comentario }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message);
      setSolicitudResponder(null);
      setComentario("");
      cargarDatos();
      setExito(`Solicitud ${estado.toLowerCase()} correctamente`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDescargarPDF = async (id) => {
    const res = await fetch(`${API_URL}/vacaciones/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vacaciones_${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCancelar = async (id) => {
    try {
      await fetch(`${API_URL}/vacaciones/${id}/cancelar`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      cargarDatos();
    } catch {
      setError("No se pudo cancelar la solicitud");
    }
  };

  const formatFecha = (f) => {
    if (!f) return "—";
    // Si viene como string YYYY-MM-DD parsea local, no UTC
    if (typeof f === "string" && f.match(/^\d{4}-\d{2}-\d{2}/)) {
      const [y, m, d] = f.split("T")[0].split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("es-MX", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return new Date(f).toLocaleDateString("es-MX", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatearFechaRenovacion = (fechaISO) => {
    if (!fechaISO) return "";

    // Creamos la fecha indicando UTC para evitar que cambie el día por la zona horaria local
    const fecha = new Date(fechaISO);

    // Extraemos día y mes en español usando UTC
    const dia = fecha.getUTCDate().toString().padStart(2, "0");
    const meses = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    const mes = meses[fecha.getUTCMonth()];

    return `${dia} de ${mes}`;
  };
  const colorEstado = {
    Pendiente: "#007bff",
    Aprobada: "#28a745",
    Rechazada: "#dc3545",
    Cancelada: "#6c757d",
  };

  const handleVerPDF = async (row) => {
    try {
      const res = await fetch(`${API_URL}/vacaciones/${row.id}/ver`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo cargar");
      const blob = await res.blob();
      setPdfUrl(URL.createObjectURL(blob));
      setPdfTitulo(`Vacaciones — solicitud #${row.id}`);
      setPdfRow(row);
    } catch (err) {
      setError(err.message);
    }
  };

  const cerrarPDF = () => {
    URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfRow(null);
  };

  if (!usuario) return null;

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 620,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            flexShrink: 0,
            borderBottom: "1px solid #ddd",
            paddingBottom: 12,
            marginBottom: 12,
          }}
        >
          {/* Flexbox para alinear el H3 a la izquierda y la fecha a la derecha */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ margin: 0 }}>
              Vacaciones — {info?.nombre_completo ?? usuario?.nombre_completo}
            </h3>
            <span style={{ fontSize: 14, color: "#555", fontWeight: 500 }}>
              Fecha de renovación:{" "}
              {info ? formatearFechaRenovacion(info.fecha_vacaciones) : "..."}
            </span>
          </div>

          {info && (
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#888" }}>
              {info.años_trabajados} año{info.años_trabajados !== 1 ? "s" : ""}{" "}
              trabajados &nbsp;·&nbsp; {info.area_nombre ?? "—"}
            </p>
          )}
        </div>

        {/* Resumen de días */}
        {info && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              marginBottom: 16,
              flexShrink: 0,
            }}
          >
            {[
              {
                label: "Días totales",
                valor: info.dias_totales,
                color: "#1e3a5f",
              },
              {
                label: "Días usados",
                valor: info.dias_usados,
                color: "#dc3545",
              },
              {
                label: "Días disponibles",
                valor: info.dias_disponibles,
                color: "#28a745",
              },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  textAlign: "center",
                  padding: "10px 8px",
                  border: `2px solid ${item.color}20`,
                  borderRadius: 6,
                  background: `${item.color}08`,
                }}
              >
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: item.color }}
                >
                  {item.valor}
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                  {item.label}
                </div>
              </div>
            ))}
            {info?.dias_periodo_pasado > 0 && (
              <div
                style={{
                  border: "2px solid #17a2b8",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 12,
                  background: "#e8f7fa",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#17a2b8",
                      }}
                    >
                      📅 Días del periodo pasado
                    </div>
                    <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                      Utilizables durante{" "}
                      {(() => {
                        const inicio = new Date(info.periodo_pasado.fecha_fin);
                        const vence = new Date(inicio);
                        vence.setDate(vence.getDate() + 30);
                        const dias = Math.ceil(
                          (vence - new Date()) / (1000 * 60 * 60 * 24),
                        );
                        return `${dias} días más`;
                      })()}
                    </div>
                  </div>
                  <div
                    style={{ fontSize: 28, fontWeight: 700, color: "#17a2b8" }}
                  >
                    {info.dias_periodo_pasado}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div
          style={{ display: "flex", gap: 4, marginBottom: 12, flexShrink: 0 }}
        >
          {[
            ...(esPropioUsuario
              ? [{ key: "solicitar", label: "Solicitar" }]
              : []),
            { key: "historial", label: `Historial (${solicitudes.length})` },
            { key: "tabla", label: "Tabla de días" },
            ...(info?.periodos?.length > 0
              ? [{ key: "periodos", label: "Periodos pasados" }]
              : []),
            ...(puedeAprobar
              ? [
                  {
                    key: "pendientes",
                    label: `Pendientes (${pendientes.length})`,
                  },
                ]
              : []),
            ...(isRRHH
              ? [{ key: "registrar", label: "Registrar manual" }]
              : []),
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setError("");
                setExito("");
              }}
              className={tab === t.key ? "btn-primary" : "btn-secondary"}
              style={{ fontSize: 12, padding: "5px 12px" }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Contenido con scroll */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* Mensajes */}
          {error && (
            <div
              style={{
                background: "#fde8e8",
                border: "1px solid #f5c6c6",
                borderRadius: 4,
                padding: "8px 12px",
                fontSize: 12,
                color: "#c0392b",
                marginBottom: 10,
              }}
            >
              {error}
            </div>
          )}
          {exito && (
            <div
              style={{
                background: "#d4edda",
                border: "1px solid #c3e6cb",
                borderRadius: 4,
                padding: "8px 12px",
                fontSize: 12,
                color: "#155724",
                marginBottom: 10,
              }}
            >
              {exito}
            </div>
          )}

          {loading && (
            <p style={{ color: "#888", fontSize: 13 }}>Cargando...</p>
          )}

          {/* ── Tab Solicitar ── */}
          {!loading && tab === "solicitar" && (
            <form onSubmit={handleSolicitar}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      color: "#666",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Fecha de inicio
                  </label>
                  <input
                    className="form-input"
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      color: "#666",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Fecha de regreso
                  </label>
                  <input
                    className="form-input"
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    min={fechaInicio || new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>
              </div>

              {/* Días calculados */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  background: "#f0f4ff",
                  border: "1px solid #c5d3f5",
                  borderRadius: 6,
                  marginBottom: 14,
                }}
              >
                <span style={{ fontSize: 13, color: "#444" }}>
                  Días hábiles a descontar
                </span>
                <span
                  style={{ fontSize: 20, fontWeight: 700, color: "#1e3a5f" }}
                >
                  {diasCalculados}
                </span>
              </div>

              {/* Advertencia si no alcanza */}
              {info &&
                diasCalculados > info.dias_disponibles &&
                tipoSolicitud === "normal" && (
                  <div
                    style={{ fontSize: 12, color: "#dc3545", marginBottom: 10 }}
                  >
                    ⚠️ No tienes suficientes días disponibles (
                    {info.dias_disponibles} disponibles, {diasCalculados}{" "}
                    solicitados)
                  </div>
                )}
              {sabadosDetectados.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#555",
                      marginBottom: 6,
                    }}
                  >
                    Sábados detectados en el periodo:
                  </div>
                  {sabadosDetectados.map((fecha) => {
                    const laborable = sabadosLaborables.includes(fecha);
                    const label = new Date(
                      ...fecha
                        .split("-")
                        .map((v, i) => (i === 1 ? v - 1 : Number(v))),
                    ).toLocaleDateString("es-MX", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    });
                    return (
                      <div
                        key={fecha}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          borderRadius: 6,
                          marginBottom: 6,
                          background: laborable ? "#e8f5e9" : "#f5f5f5",
                          border: `1px solid ${laborable ? "#a5d6a7" : "#ddd"}`,
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#444" }}>
                          📅 {label.charAt(0).toUpperCase() + label.slice(1)}
                        </span>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                          }}
                        >
                          <span style={{ fontSize: 11, color: "#888" }}>
                            {laborable ? "Laborable" : "No laborable"}
                          </span>
                          <div
                            onClick={() => toggleSabado(fecha)}
                            style={{
                              width: 40,
                              height: 22,
                              borderRadius: 11,
                              background: laborable ? "#28a745" : "#ccc",
                              position: "relative",
                              cursor: "pointer",
                              transition: "background 0.2s",
                            }}
                          >
                            <div
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                background: "white",
                                position: "absolute",
                                top: 2,
                                left: laborable ? 20 : 2,
                                transition: "left 0.2s",
                              }}
                            />
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Mostrar solo si no hay días */}
              {info && info.dias_disponibles <= 0 && (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      fontSize: 12,
                      color: "#dc3545",
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    ⚠️ No tienes días disponibles. Elige una opción:
                  </label>
                  {[
                    {
                      value: "anticipo",
                      label:
                        "📅 Anticipo del siguiente periodo (días en negativo)",
                    },
                    {
                      value: "descuento_nomina",
                      label: "💰 Descuento de nómina (sin afectar saldo)",
                    },
                  ].map((op) => (
                    <label
                      key={op.value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6,
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      <input
                        type="radio"
                        name="tipo"
                        value={op.value}
                        checked={tipoSolicitud === op.value}
                        onChange={() => setTipoSolicitud(op.value)}
                      />
                      {op.label}
                    </label>
                  ))}
                </div>
              )}

              <div
                style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
              >
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onClose}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    enviando ||
                    diasCalculados <= 0 ||
                    (tipoSolicitud === "normal" &&
                      info &&
                      diasCalculados > info.dias_disponibles) ||
                    (info &&
                      info.dias_disponibles <= 0 &&
                      tipoSolicitud === "normal")
                  }
                >
                  {enviando ? "Enviando..." : "Solicitar vacaciones"}
                </button>
              </div>
            </form>
          )}

          {/* ── Tab Historial ── */}
          {!loading &&
            tab === "historial" &&
            (solicitudes.length === 0 ? (
              <p style={{ color: "#888", fontSize: 13 }}>
                Sin solicitudes registradas
              </p>
            ) : (
              solicitudes.map((s) => (
                <div
                  key={s.id}
                  style={{
                    border: "1px solid #eee",
                    borderRadius: 6,
                    padding: "10px 14px",
                    marginBottom: 8,
                    borderLeft: `3px solid ${colorEstado[s.estado] ?? "#ccc"}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      {formatFecha(s.fecha_inicio)} —{" "}
                      {formatFecha(s.fecha_regreso)}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 20,
                        color: "white",
                        background: colorEstado[s.estado] ?? "#ccc",
                      }}
                    >
                      {s.estado}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                    {s.dias_solicitados} días hábiles
                    {s.comentario_gerente &&
                      ` · Comentario: ${s.comentario_gerente}`}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {s.estado !== "Cancelada" && s.archivo_path && (
                      <>
                        <button
                          className="btn-secondary"
                          style={{ fontSize: 11, padding: "3px 10px" }}
                          onClick={() => handleVerPDF(s)}
                        >
                          Ver
                        </button>
                        <button
                          className="btn-secondary"
                          style={{ fontSize: 11, padding: "3px 10px" }}
                          onClick={() => handleDescargarPDF(s.id)}
                        >
                          Descargar
                        </button>
                      </>
                    )}
                    {s.estado === "Pendiente" && (
                      <button
                        className="btn-delete"
                        style={{ fontSize: 11, padding: "3px 10px" }}
                        onClick={() => handleCancelar(s.id)}
                      >
                        Cancelar solicitud
                      </button>
                    )}
                  </div>
                </div>
              ))
            ))}

          {/* ── Tab Tabla ── */}
          {!loading && tab === "tabla" && (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: "#1e3a5f", color: "white" }}>
                  <th style={{ padding: "8px 12px", textAlign: "center" }}>
                    Años laborados
                  </th>
                  <th style={{ padding: "8px 12px", textAlign: "center" }}>
                    Días a disfrutar
                  </th>
                </tr>
              </thead>
              <tbody>
                {TABLA.map((fila, i) => {
                  const esActual = info && fila.dias === info.dias_totales;
                  return (
                    <tr
                      key={i}
                      style={{
                        background: esActual
                          ? "#fff9c4"
                          : i % 2 === 0
                            ? "#f9f9f9"
                            : "white",
                        fontWeight: esActual ? 700 : 400,
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <td style={{ padding: "7px 12px", textAlign: "center" }}>
                        {fila.label}
                      </td>
                      <td
                        style={{
                          padding: "7px 12px",
                          textAlign: "center",
                          color: esActual ? "#1e3a5f" : "#333",
                        }}
                      >
                        {fila.dias} {esActual ? "← tú" : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {/* Tab periodos pasados */}
          {tab === "periodos" && (
            <div>
              {info?.periodos?.length === 0 ? (
                <p style={{ color: "#888", fontSize: 13 }}>
                  Sin periodos registrados
                </p>
              ) : (
                info.periodos.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      border: "1px solid #eee",
                      borderRadius: 6,
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 13 }}>
                      {p.año}
                    </span>
                    <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
                      <span style={{ color: "#1e3a5f" }}>
                        Total: <b>{p.dias_totales}</b>
                      </span>
                      <span style={{ color: "#dc3545" }}>
                        Usados: <b>{p.dias_usados}</b>
                      </span>
                      <span
                        style={{
                          color: p.dias_pendientes > 0 ? "#17a2b8" : "#888",
                        }}
                      >
                        Pendientes: <b>{p.dias_pendientes}</b>
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Tab Pendientes (gerente/admin) ── */}
          {!loading &&
            tab === "pendientes" &&
            puedeAprobar &&
            (pendientes.length === 0 ? (
              <p style={{ color: "#888", fontSize: 13 }}>
                Sin solicitudes pendientes
              </p>
            ) : (
              pendientes.map((s) => (
                <div
                  key={s.id}
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    padding: "12px 14px",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {s.usuario_nombre}
                      </div>
                      <div style={{ fontSize: 12, color: "#666" }}>
                        {s.usuario_puesto} · {s.area_nombre}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        background: "#fff3cd",
                        color: "#856404",
                        padding: "2px 8px",
                        borderRadius: 20,
                      }}
                    >
                      Pendiente
                    </span>
                  </div>

                  <div style={{ fontSize: 12, color: "#444", margin: "8px 0" }}>
                    📅 {formatFecha(s.fecha_inicio)} —{" "}
                    {formatFecha(s.fecha_regreso)}
                    &nbsp;·&nbsp; <b>{s.dias_solicitados} días</b>
                    &nbsp;·&nbsp; {s.dias_disponibles} disponibles
                  </div>

                  {/* Formulario de respuesta */}
                  {solicitudResponder === s.id ? (
                    <div>
                      <textarea
                        className="form-input"
                        rows={2}
                        style={{ fontSize: 12, marginBottom: 8 }}
                        placeholder="Comentario (opcional)"
                        value={comentario}
                        onChange={(e) => setComentario(e.target.value)}
                      />
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn-primary"
                          style={{
                            fontSize: 12,
                            padding: "5px 14px",
                            background: "#28a745",
                          }}
                          onClick={() => handleResponder(s.id, "Aprobada")}
                        >
                          ✓ Aprobar
                        </button>
                        <button
                          className="btn-primary"
                          style={{
                            fontSize: 12,
                            padding: "5px 14px",
                            background: "#dc3545",
                          }}
                          onClick={() => handleResponder(s.id, "Rechazada")}
                        >
                          ✗ Rechazar
                        </button>
                        <button
                          className="btn-secondary"
                          style={{ fontSize: 12, padding: "5px 10px" }}
                          onClick={() => {
                            setSolicitudResponder(null);
                            setComentario("");
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: "5px 12px" }}
                        onClick={() => {
                          setSolicitudResponder(s.id);
                          setComentario("");
                        }}
                      >
                        Responder
                      </button>
                    </div>
                  )}
                </div>
              ))
            ))}

          {tab === "registrar" && (isRRHH || isAdmin) && (
            <RegistrarVacacionManual
              usuario={usuario}
              token={token}
              onExito={() => {
                setTab("historial");
                cargarDatos();
              }}
              info={info}
            />
          )}
        </div>
        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #ddd",
            paddingTop: 10,
            marginTop: 10,
            flexShrink: 0,
            textAlign: "right",
          }}
        >
          <button className="btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
      <ModalVerPDF
        url={pdfUrl}
        titulo={pdfTitulo}
        isAdmin={isAdmin}
        onDescargar={() => handleDescargarPDF(/* id actual */)}
        onClose={() => {
          URL.revokeObjectURL(pdfUrl);
          setPdfUrl(null);
        }}
      />
    </div>
  );
}
